import {
  Component,
  effect,
  viewChild,
  signal,
  ViewChild,
  inject,
  untracked,
  AfterViewInit,
  OnInit,
  ComponentRef,
  ViewContainerRef,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  ElementRef,
  DestroyRef,
  computed,
  Signal,
  ChangeDetectorRef
} from '@angular/core';
import { Router } from '@angular/router';
import { EntityBase } from '../../../_models/EntityBase';
import { MenuItemObject } from '../../models/context-menu.models';
import { IDateSelected, ListViewComponent } from '../list-view/list-view.component';
import { DataService } from '../../../services/DataService';
import { GenetricDetailComponent } from '../../edit-components/generic-detail.component';
import { IListComponent } from '../../_primitives/IListComponent';
import { DependencyInjectionProvider, EntityConstructor } from '../../../services/DependecyInjection.provider';
import { AlertModalService } from '../../../services/AlertModalService';
import { AlertModalResult } from '../../nested-components/alert-dialog/alert-dialog.component';
import { GenericComponent } from '../../_base/GenericComponent';
import { TranslationService } from '../../../services/TranslationService';
import { MatwinsVariablesService } from '../../../services/matwinsVariables.service';
import { EntityState } from '../../../_models/IChangedEntity';
import { LookupProfile, LookupService } from '../../../services/LookupService';
import { SiteTimeZoneService } from '../../../services/SiteTimeZoneService';
import { StructuredDataRequest, FilterGroupDataRequest, FilterCriteriaDataRequest } from '../../../_DTOs/IStructuredData';
import { FeatureMediatorComponent } from '../../feature-med-components/FeatureMediatorComponent';
import { PermissionsService } from '../../../services/Permissions.service';
import { ComponentInstancesService } from '../../../services/componentInstances.service';
import { SignalRNotificationService } from '../../../services/SignalRNotificationService';
import { Subscription } from 'rxjs';
import { EntityChangeInfo, EntityChangePayload, NavigationChangeType } from '../../../_models/SignalR.models';
import { CurrentTenantService } from '../../../services/CurrentTenantService';
import { FilterStateService, BuildFilterGroups } from '../../../services/FilterStateService';
import { ToInstantString, DebutDeJournee, FinDeJournee } from '../../../helpers/dateHelpers';
import { StringHelpers } from '../../../helpers/StringHelpers';
import { BuildPresetRange } from '../../../helpers/dateRangeHelpers';
import { IEntityMetaData, IPropretyAttribute } from '../../../_models/IEntityMetaData';
import { INeedRefreshData, IRefreshDateRangeValue } from '../../_primitives/INeedRefreshData';
import { IFilterCriteria, IFilterOperator } from '../../_primitives/IFilterCriteria';
import { FilterOption } from '../../nested-components/multi-criteria-filter/multi-criteria-filter.component';
import { EntityChangeLogService, ChangeEntry } from '../../../services/EntityChangeLogService';
import { IEntity } from '../../../_models/IEntity';

@Component({
  template: '',
  imports: []
})
export abstract class GenericListComponent<T extends EntityBase> extends GenericComponent<T> implements IListComponent<T>, OnInit, AfterViewInit, OnChanges {

  Editable: boolean = true;

  protected filterCriterias = signal<IFilterCriteria[]>([]);
  protected selectedFilterOptionIds = signal<string[]>([]);
  protected filterStateService = inject(FilterStateService);
  protected rootUrl = "/Documents/";

  // Vrai des que l'etat sauvegarde a ete relu, pour ne pas l'ecraser par un etat vide
  // avant la restauration.
  private filterStateRestored = false;

  // Empreinte des filtres en vigueur au moment ou le cache a ete rempli. Tant qu'elle
  // correspond, le cache repond aux filtres courants et peut servir sans requete.
  private cacheFilterFingerprint = '';
  // Total serveur vu au dernier remplissage du cache (mode API). Sans lui, un resultat
  // plus petit qu'une page ne "couvre" jamais la page demandee et chaque refresh
  // retelcharge tout le graphe client.
  private cacheServerTotal: number | null = null;

  // Total renvoye par la base pour une liste imbriquee : il fait autorite sur le comptage
  // local, qui ne voit que ce qui a ete precharge.
  protected nestedServerTotal: number | null = null;
  // Vrai quand la base a plus de correspondances que la liste n'en a charge : le resultat
  // affiche est partiel, et il faut le dire plutot que de laisser croire l'inverse.
  protected nestedTruncated = false;
  private nestedVerifyTimer: any = null;
  private readonly NESTED_VERIFY_DEBOUNCE_TIME = 700;
  // Contexte (recherche, filtres, site) couvert par la derniere verification serveur.
  // Tant qu'il correspond et que rien n'est tronque, le cache contient tout le
  // resultat : tri et pagination locaux sont exacts sans requete.
  private nestedVerifyFingerprint = '';
  // Ordre de tri couvert par la fenetre chargee : un tri different impose un nouveau
  // fetch tant que le resultat complet n'est pas en cache.
  private nestedSortKey = '';
  // Requete en vol identifiee par bloc, contexte et tri : quand un meme changement
  // arrive par deux chemins (refresh du mediateur puis fin du debounce), le second
  // declencheur n'envoie pas une copie de la requete deja partie.
  private nestedInFlightKey: string | null = null;

  // Blocs deja ramenes par nestedquery, indexes par leur offset dans le resultat.
  // Le cache grandit au fil de la navigation au lieu d'etre reconstruit a chaque
  // fetch : revenir sur une page deja vue ne repart donc pas en requete.
  private nestedBlocks = new Map<number, T[]>();

  private signalRService = inject(SignalRNotificationService);
  private signalRSubscription?: Subscription;

  public translationService = inject(TranslationService);

  // Cache local (toujours utilisé)
  protected cache: T[] = [];

  public searchTerm = signal('');

  // Ce que le filtre courant retient du cache : affichage immediat, et reference pour juger
  // ce que la base renvoie en trop ou en moins.
  protected get filteredCache(): T[] {
    return this.filterItems(this.cache);
  }

  // Criteres de categorie puis recherche locale, appliques a n'importe quel ensemble :
  // le cache complet, ou seulement les blocs qui recouvrent la page demandee.
  protected filterItems(items: T[]): T[] {
    const criterias = this.filterCriterias();
    if (criterias.length > 0) {
      const andCriterias = criterias.filter(c => c.filterLogic === 'and');
      const orCriterias = criterias.filter(c => c.filterLogic === 'or');

      items = items.filter(item => {
        const andResult = andCriterias.length === 0 || andCriterias.every(criteria => this.evaluateCriteria(item, criteria));
        const orResult = orCriterias.length === 0 || orCriterias.some(criteria => this.evaluateCriteria(item, criteria));
        return andResult && orResult;
      });
    }

    return this.applyLocalSearch(items);
  }

  // La recherche est faite cote serveur, mais on l'applique aussi en local : c'est ce qui
  // donne un resultat immediat pendant que la requete est en vol.
  protected applyLocalSearch(items: T[]): T[] {
    const searchTerm = StringHelpers.NormalizeForSearch(this.listView()?.searchTerm()).trim();
    if (!searchTerm) return items;

    const searchColumns = this.listView()?.getPagination().searchColumns ?? [];
    if (!searchColumns.length) return items;

    return items.filter(item => searchColumns.some(column => {
      const value = this.getNestedPropertyValue(item, column);
      return value != null && StringHelpers.NormalizeForSearch(value.toString()).includes(searchTerm);
    }));
  }

  // Cle stable de la liste, utilisee pour conserver son etat de filtres.
  public get listKey(): string {
    const namespace = this.entityMetaData.Namespace ? `${this.entityMetaData.Namespace}.` : '';
    return `${namespace}${this.entityMetaData.EntityName}`;
  }

  protected restoreFilterState(): void {
    this.filterStateRestored = true;

    const listView = this.listView();
    if (!listView) return;

    const state = this.filterStateService.get(this.listKey);
    if (!state) return;

    listView.searchTerm.set(state.searchTerm ?? '');
    this.selectedFilterOptionIds.set(state.selectedOptionIds ?? []);
    this.filterCriterias.set(state.filterCriterias ?? []);

    // Une plage nommee se recalcule sur le jour courant : "Aujourd'hui" doit rester aujourd'hui
    // meme si le choix date de la veille. Une periode libre, elle, se relit telle quelle.
    const presetRange = state.dateRangePreset ? BuildPresetRange(state.dateRangePreset) : null;
    const customRange = state.startDate && state.endDate
      ? { startDate: new Date(state.startDate), endDate: new Date(state.endDate) }
      : null;
    const dateRange = presetRange ?? customRange;

    if (dateRange) {
      listView.dateSelected.set({ ...dateRange, preset: state.dateRangePreset ?? undefined });
      // Le calendrier lit ses bornes dans ces inputs, pas dans dateSelected : sans
      // cette synchro il continuerait d'afficher aujourd'hui malgre la plage restauree.
      listView.selectedStartDate = new Date(dateRange.startDate);
      listView.selectedEndDate = new Date(dateRange.endDate);
    }
  }

  private currentFilterFingerprint(): string {
    return JSON.stringify({
      criterias: this.filterCriterias(),
      dateSelected: this.listView()?.dateSelected() ?? null
    });
  }

  // Un groupe par champ : les puces d'un meme champ s'unissent en OR, deux champs distincts
  // se combinent en AND. C'est ce que l'utilisateur attend de deux criteres differents.
  protected buildServerFilters(): FilterGroupDataRequest[] {
    const groups = BuildFilterGroups(this.filterCriterias());

    const dateSelected = this.listView()?.dateSelected();
    if (dateSelected?.startDate) {
      const column = Object.values(this.entityMetaData.PropretyDescriptors)
        .find(prop => prop.PropretyAttributes.includes(IPropretyAttribute.DateRangeFilterColumn));

      if (column) {
        // Une borne par groupe : les criteres d'un meme groupe s'unissent en OR, et
        // ">= debut OU <= fin" est toujours vrai - le filtre ne filtrerait rien.
        groups.push({
          groupName: `${column.PropretyName}_from`,
          criteria: [{
            fieldName: column.PropretyName,
            operator: 'gte',
            value: DebutDeJournee(new Date(dateSelected.startDate))
          }]
        });

        if (dateSelected.endDate) {
          groups.push({
            groupName: `${column.PropretyName}_to`,
            criteria: [{
              fieldName: column.PropretyName,
              operator: 'lte',
              value: FinDeJournee(new Date(dateSelected.endDate))
            }]
          });
        }
      }
    }

    return groups;
  }

  protected persistFilterState(): void {
    if (!this.filterStateRestored) return;

    const listView = this.listView();
    if (!listView) return;

    const dateSelected = listView.dateSelected();
    const isCustomRange = dateSelected?.preset === 'Custom';

    this.filterStateService.set(this.listKey, {
      searchTerm: listView.searchTerm() ?? '',
      dateRangePreset: dateSelected?.preset ?? null,
      // Seule une periode libre a des bornes qu'on ne sait pas recalculer.
      startDate: isCustomRange && dateSelected?.startDate ? ToInstantString(new Date(dateSelected.startDate)) : null,
      endDate: isCustomRange && dateSelected?.endDate ? ToInstantString(new Date(dateSelected.endDate)) : null,
      selectedOptionIds: this.selectedFilterOptionIds(),
      filterCriterias: this.filterCriterias()
    });
  }

  private evaluateCriteria(item: any, criteria: IFilterCriteria): boolean {
    const value = (item as any)[criteria.fieldName];
    switch (criteria.filterOperator) {
      case IFilterOperator.equals:
        return value === criteria.fieldValue || `${value}`.trim() == `${criteria.fieldValue}`.trim();
      case IFilterOperator.notEquals:
        return value !== criteria.fieldValue && `${value}`.trim() !== `${criteria.fieldValue}`.trim();
      case IFilterOperator.greaterThan:
        return value > criteria.fieldValue;
      case IFilterOperator.lessThan:
        return value < criteria.fieldValue;
      case IFilterOperator.greaterThanOrEqual:
        return value >= criteria.fieldValue;
      case IFilterOperator.lessThanOrEqual:
        return value <= criteria.fieldValue;
      case IFilterOperator.contains:
        return value?.includes(criteria.fieldValue);
      case IFilterOperator.notContains:
        return !value?.includes(criteria.fieldValue);
      case IFilterOperator.startsWith:
        return value?.startsWith(criteria.fieldValue);
      case IFilterOperator.endsWith:
        return value?.endsWith(criteria.fieldValue);
      default:
        return true;
    }
  }

  protected cacheSize = 300;
  protected cachePage = 1;

  // Decalage absolu de la premiere ligne detenue : le cache represente le bloc de lignes
  // [cacheStart, cacheStart + cache.length) du resultat serveur.
  protected cacheStart = 0;

  protected lookupService = inject(LookupService);
  protected siteTimeZoneService = inject(SiteTimeZoneService);

  public static get lookupProfiles(): LookupProfile[] {
    return [];
  }

  protected get instanceLookupProfiles(): LookupProfile[] {
    return (this.constructor as typeof GenericListComponent).lookupProfiles;
  }

  public override get RequiredPermissions(): string[] {
    let permissions = [];
    let namespace = this.entityMetaData.Namespace ? `${this.entityMetaData.Namespace}.` : '';
    permissions.push(`Permissions.Entities.${namespace}${this.entityMetaData.EntityName}.Read`);
    return permissions;
  }

  public override get HasRequiredPermissions(): boolean {
    return this.permissionsService.UserHasAllPermissions(this.RequiredPermissions);
  }


  private _isNested = false;

  @Input()
  set isNested(value: boolean) {
    if (value === this._isNested) return;

    this._isNested = value;

    if (value) {
      this.dataMode = 'nested';
    }
  }

  get isNested(): boolean {
    return this._isNested;
  }


  @Input() nestedData: T[] | null = null;
  @Output() itemSelected = new EventEmitter<T | null>();

  //  uniquement 'api' et 'nested'
  @Input() dataMode: 'api' | 'nested' = 'api';

  @Input() featureContext: string | null = null;
  @Input() includedEntities: string[] = [];

  public varsService = inject(MatwinsVariablesService);
  protected _fkPointOfServiceId: number | null = null;
  @Input() set fkPointOfServiceId(value: number | null) {
    const precedent = this._fkPointOfServiceId;
    this._fkPointOfServiceId = value;
    this.varsService.setPosId(value);

    // Le total serveur suit le site COURANT : sans ce rappel, changer de site gardait l'ancien total.
    if (this.isNested && precedent !== value) {
      const listView = this.listView();
      if (listView && this.hasActiveQuery()) this.scheduleNestedVerification(listView);
    }
  }
  get fkPointOfServiceId(): number | null {
    return this._fkPointOfServiceId;
  }

  protected di = inject(DependencyInjectionProvider);
  router = inject(Router);

  protected destroyRef = inject(DestroyRef);
  public dataService!: DataService<T>;

  listView = viewChild<ListViewComponent<T>>(ListViewComponent);
  selectedItem = signal<T | null>(null);
  isModalOpen = signal(false);
  itemDetail = viewChild('detailComponent', { read: GenetricDetailComponent });

  @ViewChild('navigationContainer', { read: ViewContainerRef, static: false }) navigationContainer!: ViewContainerRef;

  protected navigationEntities: EntityConstructor<any>[] = [];
  protected navigationComponents = new Map<string, ComponentRef<IListComponent<any>>>();

  protected DETAIL_MIN_WIDTH: number = 400;
  @ViewChild('container', { static: false }) detailViewContainer!: ElementRef<HTMLElement>;
  protected minWidth = this.DETAIL_MIN_WIDTH;
  protected currentWidth = signal(this.DETAIL_MIN_WIDTH);
  protected startX = 0;
  protected startWidth = 0;

  loading = signal(false);
  loadDebounceTimer: any = null;
  // Un changement arrive pendant une requete en vol : on ne peut pas lancer le fetch
  // tout de suite, mais l'ignorer laisserait l'ancien filtre applique (croix qui vide
  // le champ sans rafraichir la liste). Il est rejoue a la fin du chargement.
  private pendingLoadAfterInFlight = false;
  readonly SEARCH_DEBOUNCE_TIME = 1000;
  readonly ACTION_DEBOUNCE_TIME = 100;
  readonly MAX_SEARCH_TIMEOUT = 10000;
  private searchTimeout?: any;

  private previousDateSelected = signal<IDateSelected | null>(null);
  private previousSearchTerm = signal<string>('');
  private previousSortBy = signal<string>('');
  private previousSortOrder = signal<'asc' | 'desc'>('asc');
  private previousPage = signal<number>(1);
  private previousPageSize = signal<number>(10);
  private nestedDataChanged = signal(0);

  contextMenuItems: MenuItemObject<T>[] = [
    {
      itemKey: 'details',
      itemText: 'Details',
      action: (item: T) => this.showDetails(item)
    },
    {
      itemKey: 'edit',
      itemText: 'Modifier',
      action: (item: T) => this.editSelectedItem(item),
      disabled: (item: T) => !this.canEdit(item)
    },
    {
      itemKey: 'delete',
      itemText: 'Supprimer',
      action: (item: T) => {
        alert();
        return this.deleteSelectedItem(item);
      }
    }
  ];

  public alertModalService?: AlertModalService;
  protected mediator?: FeatureMediatorComponent | null = null;
  protected componentInstancesService: ComponentInstancesService = inject(ComponentInstancesService);
  public permissionsService: PermissionsService = inject(PermissionsService);


  protected changeLog = inject(EntityChangeLogService);       //  CHANGE LOG Remplace EntityStore
  private lastDataTimestamp: Date | null = null;
  private lastKnownClearTime: Date | null = null;

  public cdr = inject(ChangeDetectorRef);

  constructor() {
    super();

    this.mediator = inject(FeatureMediatorComponent, { optional: true });

    // Effet : rafraîchissement demandé par le mediator
    effect(() => {
      let needRefreshData = this.mediator?.needRefreshData();
      if (needRefreshData) {
        this.listView()?.refresh();
        this.cdr.detectChanges();
      }
    });

    // Effet : changement de date range
    effect(() => {
      const currentListView = this.listView();
      if (currentListView) {
        const dateSelected = currentListView.dateSelected();
        if (this.previousDateSelected() !== null &&
          JSON.stringify(this.previousDateSelected()) !== JSON.stringify(dateSelected)) {
          // Plage effacee : on retire le filtre, sinon le mediateur resterait sur la
          // derniere plage et continuerait de l'appliquer aux requetes suivantes.
          if (!dateSelected) {
            this.mediator?.needRefreshData.set(null);
          } else {
            const dateRangeFilterColumn = Object.values(this.entityMetaData.PropretyDescriptors).find(prop => prop.PropretyAttributes.includes(IPropretyAttribute.DateRangeFilterColumn));
            if (dateRangeFilterColumn) {
              const needRefreshData: INeedRefreshData = {
                entityName: this.entityMetaData.EntityName,
                columnName: dateRangeFilterColumn?.PropretyName,
                filterType: 'dateTime',
                value: {} as IRefreshDateRangeValue
              };
              (needRefreshData.value as IRefreshDateRangeValue).startDate = dateSelected.startDate;
              (needRefreshData.value as IRefreshDateRangeValue).endDate = dateSelected.endDate || dateSelected.startDate;
              this.mediator?.needRefreshData.set(needRefreshData);
            }
          }

          // Une liste imbriquee n'a pas de mediateur pour la relancer : sans cet appel, la
          // nouvelle plage ne serait jamais appliquee en base.
          if (this.isNested && this.hasActiveQuery()) {
            this.scheduleNestedVerification(currentListView);
          }
        }
        this.previousDateSelected.set(dateSelected);
        this.persistFilterState();
      }
    });

    // Effet : remise a zero globale des filtres demandee depuis la vue Admin
    effect(() => {
      const clearedAt = this.filterStateService.clearedAt();
      if (!clearedAt) return;

      untracked(() => {
        const listView = this.listView();
        if (!listView) return;

        this.filterCriterias.set([]);
        this.selectedFilterOptionIds.set([]);
        listView.searchTerm.set('');
        listView.dateSelected.set(null);
        listView.refresh();
      });
    });

    // Effet : debounce de chargement
    effect(() => {
      const currentListView = this.listView();
      if (currentListView) {
        const refresh = currentListView.refreshCounter();
        const search = currentListView.searchTerm();
        const sortBy = currentListView.sortBy();
        const sortOrder = currentListView.sortOrder();
        const page = currentListView.page();
        const pageSize = currentListView.pageSize();

        untracked(() => {
          this.searchTerm.set(search);
          this.persistFilterState();

          let debounceTime = this.isNested ? 0 : this.ACTION_DEBOUNCE_TIME;
          const searchChanged = search !== this.previousSearchTerm();
          const sortChanged = sortBy !== this.previousSortBy() || sortOrder !== this.previousSortOrder();
          const pageChanged = page !== this.previousPage() || pageSize !== this.previousPageSize();

          if (searchChanged || sortChanged) {
            if (!this.isNested) {
              this.cachePage = 1;
              // Recherche et tri sont appliqués côté serveur : sans invalidation du cache,
              // loadData() sert la page depuis le cache et la requête n'est jamais rejouée.
              this.cache = [];
              this.lastDataTimestamp = null;
              this.cacheServerTotal = null;
            }
            if (searchChanged && !this.isNested) {
              debounceTime = this.SEARCH_DEBOUNCE_TIME;
              currentListView.searchState.set('typing');
            }
          }

          this.previousSearchTerm.set(search);
          this.previousSortBy.set(sortBy);
          this.previousSortOrder.set(sortOrder);
          this.previousPage.set(page);
          this.previousPageSize.set(pageSize);

          if (this.loadDebounceTimer) clearTimeout(this.loadDebounceTimer);
          // Toute action directe (tri, page, puce) declenche le fetch sans debounce.
          // Le debounce ne sert qu'a coalescer la frappe dans la recherche.
          const immediateVerify = !searchChanged;
          this.loadDebounceTimer = setTimeout(() => {
            if (!this.isNested && this.loading()) {
              this.pendingLoadAfterInFlight = true;
            } else {
              this.executeLoadItems(currentListView, immediateVerify);
            }
          }, debounceTime);
        });
      }
    });

    // Effet : nested data race condition fix
    effect(() => {
      const listView = this.listView();
      this.nestedDataChanged();
      if (listView && this.isNested && this.nestedData) {
        untracked(() => {
          this.applyLocalFilterSortPaginate();
        });
      }
    });

    // Effet : réaction au vidage du ChangeLog
    effect(() => {
      const clearedAt = this.changeLog.lastClearedAt();
      if (clearedAt && (!this.lastKnownClearTime || clearedAt > this.lastKnownClearTime)) {
        this.lastKnownClearTime = clearedAt;
        this.lastDataTimestamp = null;
        this.listView()?.refresh();
      }
    });

    //effect(() => {
    //  if (this.isNested && this.nestedData) {
    //    // Mise à jour du cache local
    //    this.cache = this.nestedData.map(item => this.hydrateEntity(item));
    //    this.updateLastDataTimestampFromCache();
    //    this.applyPendingChanges(); // Vérifier les changements WebSocket
    //    this.applyLocalFilterSortPaginate();
    //  }
    //});
  }
  public refreshData(needRefreshData: INeedRefreshData): void {
    this.listView()?.refresh();
    this.cdr.detectChanges();
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.dataService = this.di.getOrCreateDataService(this.ComponentEntityClass);

    // Chaque liste s'enregistre elle-même au ChangeLog
    this.changeLog.registerEntityType(this.entityMetaData.EntityName);


    // Souscription WebSocket 
    this.signalRSubscription = this.signalRService.onEntityChanges(this.entityMetaData.EntityName)
      .subscribe(({ initiators, changes }) => {
        this.handleFilteredEntityChanges(initiators, changes);
      });
  }

  override ngOnDestroy() {
    if (this.nestedVerifyTimer) clearTimeout(this.nestedVerifyTimer);
    this.signalRSubscription?.unsubscribe();
    super.ngOnDestroy();
  }

  override ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);
    if (changes['nestedData'] && this.isNested && this.nestedData) {
      this.cache = this.nestedData.map(item => this.hydrateEntity(item));
      this.updateLastDataTimestampFromCache();
      // Le cache reconstruit ne beneficie plus d'une verification anterieure.
      this.nestedServerTotal = null;
      this.nestedTruncated = false;
      this.nestedVerifyFingerprint = '';
      this.nestedSortKey = '';
      this.cacheStart = 0;
      // Le prechargement tient lieu de premier bloc en attendant la verification.
      this.nestedBlocks.clear();
      if (this.cache.length > 0) this.nestedBlocks.set(0, [...this.cache]);
      this.nestedDataChanged.update(v => v + 1);
    }
  }

  override ngAfterViewInit(): void {
    super.ngAfterViewInit();
    if (this.navigationEntities && this.navigationEntities.length && this.navigationContainer) {
      for (const ent of this.navigationEntities) {
        this.getNavigationList(ent);
      }
    }
    var itemDetail = this.getdetailComponent();
    if (itemDetail) itemDetail.Initiator = this;
    this.handleDetailContainerWidth();

    setTimeout(() => {
      // Restaure avant le premier chargement : la requete initiale porte deja les filtres.
      this.restoreFilterState();
      this.listView()?.refresh();
      this.cdr.detectChanges();
    }, 100);
  }

  protected handleDetailContainerWidth() {
    if (this.detailViewContainer?.nativeElement) {
      const computedMinWidth = parseFloat(getComputedStyle(this.detailViewContainer.nativeElement).minWidth);
      this.minWidth = isNaN(computedMinWidth) ? 350 : computedMinWidth;
      this.currentWidth.set(this.minWidth);
    }
  }

  protected startResize(event: MouseEvent): void {
    event.preventDefault();
    this.startX = event.clientX;
    this.startWidth = this.currentWidth();
    document.addEventListener('mousemove', this.onMouseMove);
    document.addEventListener('mouseup', this.onMouseUp);
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'ew-resize';
  }

  protected onMouseMove = (e: MouseEvent): void => {
    const delta = this.startX - e.clientX;
    const newWidth = Math.max(this.startWidth + delta, this.minWidth);
    this.currentWidth.set(newWidth);
  };

  protected onMouseUp = (): void => {
    document.removeEventListener('mousemove', this.onMouseMove);
    document.removeEventListener('mouseup', this.onMouseUp);
    document.body.style.userSelect = '';
    document.body.style.cursor = '';
  };

  protected getNavigationList<E extends EntityBase>(entityType: EntityConstructor<E>): IListComponent<E> | undefined {
    const name = entityType.getMetaData().EntityName;
    const existingRef = this.navigationComponents.get(name);
    if (existingRef) return existingRef.instance as IListComponent<E>;
    if (this.navigationContainer) {
      try {
        const compRef = this.di.createRegisteredComponentInContainer(entityType, this.navigationContainer);
        this.navigationComponents.set(name, compRef);
        return compRef.instance as IListComponent<E>;
      } catch (err) {
        console.warn(`Impossible de créer le composant de navigation pour ${name}:`, err);
        return undefined;
      }
    }
    console.warn(`navigationContainer n'est pas disponible pour créer ${name}.`);
    return undefined;
  }


  executeLoadItems(listView: ListViewComponent<T>, immediateVerify = false): void {
    // Mode nested : les donnees viennent du prechargement
    if (this.dataMode === 'nested') {
      // Retour a l'etat sans filtre apres une recherche serveur : fetchNestedBlock avait
      // remplace le cache par les seules lignes filtrees. Sans restauration du
      // prechargement, vider la recherche laisse le tableau fige sur l'ancien filtre.
      if (!this.hasActiveQuery() && this.nestedVerifyFingerprint !== '') {
        if (this.nestedVerifyTimer) {
          clearTimeout(this.nestedVerifyTimer);
          this.nestedVerifyTimer = null;
        }
        const preloaded = (this.nestedData ?? []).map(item => {
          const existing = this.cache.find(e => this.idsEqual(e.id, item.id));
          return existing ?? this.hydrateEntity(item);
        });
        const preloadedIds = new Set(preloaded.map(e => `${e.id}`));
        const pendingAdded = this.cache.filter(e => !preloadedIds.has(`${e.id}`) && e._entityState === EntityState.Added);
        this.cache = [...preloaded, ...pendingAdded];
        this.nestedServerTotal = null;
        this.nestedTruncated = false;
        this.nestedVerifyFingerprint = '';
        this.nestedSortKey = '';
        this.nestedBlocks.clear();
        if (this.cache.length > 0) this.nestedBlocks.set(0, [...this.cache]);
        this.updateLastDataTimestampFromCache();
      } else if (this.cache.length === 0 && this.nestedData?.length) {
        // ngOnChanges resynchronise deja le cache quand nestedData change : ne reconstruire
        // qu'en secours. Le reconstruire a chaque refresh jette les lignes que la
        // verification serveur avait fusionnees (pages vides en attendant la reponse).
        this.cache = this.nestedData.map(item => this.hydrateEntity(item));
        this.updateLastDataTimestampFromCache();
        if (this.nestedBlocks.size === 0) this.nestedBlocks.set(0, [...this.cache]);
      }
      this.applyPendingChanges();

      // Sans filtre, la liste se contente du prechargement ; sinon le cache n'est qu'une
      // fenetre du resultat serveur : la page demandee declenche le fetch du bloc qui la
      // contient, que l'utilisateur y arrive en avancant ou en sautant a la fin.
      const pageNonCouverte = this.hasActiveQuery() && !this.nestedCacheCoversPage(listView);
      if (pageNonCouverte) {
        const blockStart = this.nestedMissingBlockStart(listView);
        if (immediateVerify) {
          void this.fetchNestedBlock(listView, blockStart);
        } else {
          this.scheduleNestedVerification(listView);
        }
      }

      // Si la page n'est pas encore chargee, garder les lignes courantes le temps du fetch.
      this.applyLocalFilterSortPaginate(pageNonCouverte);
      // A une page de la fin des blocs charges : anticiper le bloc suivant plutot
      // que de laisser l'utilisateur attendre quand il l'atteint.
      this.prefetchNestedNext(listView);
      return;
    }

    // Mode API : requête directe backend
    listView.searchState.set('searching');
    this.searchTimeout = setTimeout(() => {
      listView.searchState.set('idle');
    }, this.MAX_SEARCH_TIMEOUT);
    this.loading.set(true);

    this.loadData(
      listView.page(),
      listView.pageSize(),
      listView.searchTerm(),
      listView.sortBy(),
      listView.sortOrder(),
      listView.getPagination().searchColumns
    ).finally(() => {
      this.loading.set(false);
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
        this.searchTimeout = undefined;
      }
      listView.searchState.set('idle');
      // Un changement (recherche videe, tri...) est arrive pendant la requete : on
      // rejoue le fetch sur l'etat courant plutot que d'afficher l'ancien filtre.
      if (this.pendingLoadAfterInFlight) {
        this.pendingLoadAfterInFlight = false;
        this.executeLoadItems(listView);
      }
    });
  }

  async loadData(page: number, pageSize: number, searchTerm: string, sortBy: string,
    sortOrder: 'asc' | 'desc', searchColumns: string[] | null): Promise<void> {
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;

    // Le cache ne vaut que pour les filtres qui l'ont rempli. On le conserve malgre tout :
    // le filtrage local continue d'afficher pendant l'attente.
    const filterFingerprint = this.currentFilterFingerprint();

    // Chargement par blocs : un bloc couvre plusieurs pages, donc plus de plafond sur le
    // nombre de pages atteignables.
    const blockStart = Math.floor(startIndex / this.cacheSize) * this.cacheSize;

    const cacheCoversPage = this.cacheStart === blockStart
      && this.cacheFilterFingerprint === filterFingerprint
      && this.lastDataTimestamp !== null
      && (this.cache.length >= endIndex - blockStart
        // Le cache atteint la fin du resultat : il couvre toute page, meme s'il detient
        // moins de lignes qu'une page (petits resultats : 2 clients sur pageSize 10).
        || (this.cacheServerTotal !== null && this.cacheStart + this.cache.length >= this.cacheServerTotal));

    if (cacheCoversPage) {
      this.applyLocalPagination(startIndex, pageSize);
      return;
    }

    // Le nom doit correspondre à `searchColumns` du DTO : ApiRepository.toDataRequest()
    // lit cette propriété, un autre nom est silencieusement ignoré (recherche inopérante).
    const combinedSearchColumns = searchColumns?.join(',');
    const refreshAllData = this.mediator?.needRefreshData();
    const lookupContexts = this.lookupService.getRequiredLookups(this.getEffectiveLookupProfiles());

    const params: StructuredDataRequest = {
      pageNumber: blockStart / this.cacheSize + 1,
      pageSize: this.cacheSize,
      search: searchTerm.toLowerCase(),
      sortBy,
      sortOrder,
      searchColumns: combinedSearchColumns,
      requiredLookups: lookupContexts,
      dateRange: refreshAllData ? [{
        startDate: ToInstantString((refreshAllData.value as IRefreshDateRangeValue).startDate),
        endDate: ToInstantString((refreshAllData.value as IRefreshDateRangeValue).endDate),
        entityName: refreshAllData.entityName,
        columnName: refreshAllData.columnName
      }] : [],
      filters: this.buildServerFilters(),
      // Les correspondances locales ne portent que sur les puces : la plage de dates n'est
      // pas appliquee en local. On ne les envoie donc que lorsqu'elles ont un sens.
      localMatchedIds: this.filterCriterias().length > 0
        ? this.filteredCache.map(item => `${item.id}`)
        : undefined,
      featureContext: this.featureContext,
      includedEntities: this.includedEntities
    };

    // On affiche le filtrage local avant d'attendre la reponse : la requete ne fait que
    // confirmer ou corriger. Attendre pour afficher viderait le filtrage local de son sens.
    this.applyLocalPagination(startIndex, pageSize);

    await this.performLoadData(params, startIndex, pageSize, filterFingerprint, blockStart);
  }

  async performLoadData(params: StructuredDataRequest, startIndex: number, pageSize: number,
    filterFingerprint: string = '', cacheStart: number = 0) {
    try {
      const requestedLookups = this.getEffectiveLookupProfiles();
      const response = await this.dataService.getAll(params, requestedLookups);
      this.cacheServerTotal = response.total;

      // Le serveur ne renvoie des identifiants que lorsqu'il a verifie que nos correspondances
      // locales sont exactement les siennes : rien a corriger, seul le total peut avoir bouge.
      if (response.matchedIds) {
        this.applyLocalPagination(startIndex, pageSize, response.total);
        return;
      }

      const newItems = response.items;
      const newCache: T[] = [];

      for (const newItem of newItems) {
        const existing = this.cache.find(e => e.id === newItem.id);
        if (existing) {
          Object.assign(existing, newItem);
          newCache.push(existing);
        } else {
          const hydrated = this.hydrateEntity(newItem);
          hydrated._entityState = EntityState.Unchanged;
          hydrated._modifiedProperties.clear();
          newCache.push(hydrated);
        }
      }

      this.cache = newCache;
      this.cacheFilterFingerprint = filterFingerprint;
      this.cacheStart = cacheStart;
      this.updateLastDataTimestampFromCache();
      this.applyPendingChanges();
      this.applyLocalPagination(startIndex, pageSize, response.total);
    } catch (error) {
      console.error(`Error loading cache for ${this.entityMetaData.EntityDisplayPlural}:`, error);
    }
  }


  private updateLastDataTimestampFromCache(): void {
    if (this.cache.length === 0) {
      this.lastDataTimestamp = new Date(0);
      return;
    }
    const maxDate = this.cache.reduce((max, item) => {
      const itemDate = (item as any)._modifiedAt || new Date((item as any).loG_Modified);
      if (!itemDate) return max;
      const dt = new Date(itemDate);
      return dt > max ? dt : max;
    }, new Date(0));

    let realMaxDate = maxDate.getTime() > 0 ? maxDate : new Date(0);
    this.lastDataTimestamp = realMaxDate;
  }

  private getCurrentTenantId(): string {
    return this.varsService.clientId || '';
  }

  private applyPendingChanges(): void {
    const tenantId = this.getCurrentTenantId();
    if (!tenantId) return;
    const changes = this.changeLog.getChangesSince(
      tenantId,
      this.entityMetaData.EntityName,
      this.lastDataTimestamp
    );
    if (changes.length === 0) return;

    // Construire un objet initiators minimal (sans componentId, ni posId, ni browserId)
    const fakeInitiators = { clientId: tenantId };
    // Extraire les payloads bruts
    const payloads = changes.map(entry => entry.payload);
    // Réutiliser la méthode existante de traitement des WebSocket
    this.handleFilteredEntityChanges(fakeInitiators, payloads);

    // Mettre à jour le timestamp après application (prend en compte les modifications éventuelles)
    this.updateLastDataTimestampFromCache();

    // (Le tri et la pagination sont déjà effectués dans handleFilteredEntityChanges,
    // mais on les refait par précaution)
    this.applySortToCache();
    const lv = this.listView();
    if (lv) {
      if (this.isNested) {
        this.applyLocalFilterSortPaginate(this.hasActiveQuery() && !this.nestedCacheCoversPage(lv));
      } else {
        this.applyLocalPagination((lv.page() - 1) * lv.pageSize(), lv.pageSize());
      }
    }
  }



  protected getEffectiveLookupProfiles(): LookupProfile[] {
    let profiles = [...this.instanceLookupProfiles];
    if (this.mediator && !this.isNested) {
      if (this.mediator.shouldFetchLookupsOnRootSelect) {
        profiles = [...profiles, ...this.mediator.otherLookupProfiles()];
      }
    }
    return this.uniqueLookupProfiles(profiles);
  }

  private uniqueLookupProfiles(profiles: LookupProfile[]): LookupProfile[] {
    const seen = new Set<string>();
    return profiles.filter(p => {
      if (seen.has(p.lookupName)) return false;
      seen.add(p.lookupName);
      return true;
    });
  }

  protected applyLocalPagination(startIndex: number, pageSize: number, serverTotal?: number): void {
    const sourceData = this.filteredCache;
    // startIndex est un decalage absolu dans le resultat ; le cache commence a cacheStart.
    const relativeStart = Math.max(0, startIndex - this.cacheStart);
    const paginatedItems = sourceData.slice(relativeStart, relativeStart + pageSize);
    const total = serverTotal ?? sourceData.length;
    this.listView()!.entities.set({ items: paginatedItems, total });
  }

  // Icone d'horloge : fuseau d'apres le site de CHAQUE ligne — voir SiteTimeZoneService.
  protected get fuseauxParSite(): Map<number, string> {
    return this.siteTimeZoneService.fuseauxParSite();
  }

  // Fuseau du site de CETTE ligne, pour les colonnes que `list-table` ne rend pas.
  protected fuseauDeLigne(entity: unknown): string | null {
    const fk = (entity as { fK_PointOfService?: number | string } | null)?.fK_PointOfService;
    if (fk == null) return null;
    return this.fuseauxParSite.get(Number(fk)) ?? null;
  }

  // Un filtre ou une recherche est pose : c'est ce qui declenche la verification.
  protected hasActiveQuery(): boolean {
    if (this.listView()?.searchTerm()?.trim()) return true;
    if (this.filterCriterias().length > 0) return true;

    // La plage de dates vaut "aujourd'hui" par defaut : des que l'entite en porte une, elle
    // s'applique en permanence et doit donc etre confrontee a la base.
    return this.hasDateRangeColumn();
  }

  private hasDateRangeColumn(): boolean {
    return Object.values(this.entityMetaData.PropretyDescriptors)
      .some(prop => prop.PropretyAttributes.includes(IPropretyAttribute.DateRangeFilterColumn));
  }

  private scheduleNestedVerification(listView: ListViewComponent<T>): void {
    if (this.nestedVerifyTimer) clearTimeout(this.nestedVerifyTimer);
    this.nestedVerifyTimer = setTimeout(() => {
      // Un fetch immediat a pu couvrir la page pendant le debounce : ne repartir
      // que si elle reste non couverte a l'expiration du delai.
      if (!this.nestedCacheCoversPage(listView)) {
        void this.fetchNestedBlock(listView, this.nestedMissingBlockStart(listView));
      }
    }, this.NESTED_VERIFY_DEBOUNCE_TIME);
  }

  private currentNestedQueryFingerprint(): string {
    const listView = this.listView();
    return JSON.stringify({
      search: listView?.searchTerm() ?? '',
      criterias: this.filterCriterias(),
      date: listView?.dateSelected() ?? null,
      pos: this.fkPointOfServiceId
    });
  }

  private currentNestedSortKey(listView: ListViewComponent<T>): string {
    return `${listView.sortBy() ?? ''}:${listView.sortOrder() ?? 'asc'}`;
  }

  // Bloc de cacheSize lignes qui contient la page demandee.
  private nestedBlockStart(listView: ListViewComponent<T>): number {
    const startIndex = (listView.page() - 1) * listView.pageSize();
    return Math.floor(startIndex / this.cacheSize) * this.cacheSize;
  }

  // Vrai quand le cache peut servir la page demandee sans requete : soit le resultat
  // complet du contexte courant y figure, soit le bloc qui contient la page a deja
  // ete ramene dans l'ordre demande.
  private nestedCacheCoversPage(listView: ListViewComponent<T>): boolean {
    if (this.nestedVerifyFingerprint !== this.currentNestedQueryFingerprint()) return false;
    if (!this.nestedTruncated) return true;
    if (this.nestedSortKey !== this.currentNestedSortKey(listView)) return false;

    const startIndex = (listView.page() - 1) * listView.pageSize();
    const lastIndex = startIndex + listView.pageSize() - 1;
    // La page peut chevaucher deux blocs : tous les blocs qu'elle touche sont requis.
    for (let b = Math.floor(startIndex / this.cacheSize) * this.cacheSize; b <= lastIndex; b += this.cacheSize) {
      if (!this.nestedBlocks.has(b)) return false;
    }
    return true;
  }

  // Premier bloc manquant dans la plage de la page. Si le contexte a change (tri ou
  // filtre), les blocs en cache n'ont plus de position valide : on reprend le bloc
  // qui contient le debut de la page.
  private nestedMissingBlockStart(listView: ListViewComponent<T>): number {
    const startBlock = this.nestedBlockStart(listView);
    const contextOk = this.nestedVerifyFingerprint === this.currentNestedQueryFingerprint()
      && this.nestedSortKey === this.currentNestedSortKey(listView);
    if (!contextOk) return startBlock;
    const lastIndex = (listView.page() - 1) * listView.pageSize() + listView.pageSize() - 1;
    for (let b = startBlock; b <= lastIndex; b += this.cacheSize) {
      if (!this.nestedBlocks.has(b)) return b;
    }
    return startBlock;
  }

  // Prefetch : quand la page demandee se termine a moins d'une page de la fin des
  // blocs charges, le bloc suivant part en requete avant que l'utilisateur ne
  // l'atteigne — la navigation reste instantanee en avancant.
  private prefetchNestedNext(listView: ListViewComponent<T>): void {
    if (!this.hasActiveQuery() || this.nestedServerTotal === null) return;
    // Contexte ou tri invalide : les blocs n'ont plus de sens pour la requete courante.
    if (this.nestedVerifyFingerprint !== this.currentNestedQueryFingerprint()) return;
    if (this.nestedSortKey !== this.currentNestedSortKey(listView)) return;

    const startIndex = (listView.page() - 1) * listView.pageSize();
    // Fin des donnees contigues a partir du bloc de la page : le premier bloc
    // manquant est exactement celui qu'il faudra quand on l'approche.
    let loadedEnd = Math.floor(startIndex / this.cacheSize) * this.cacheSize;
    while (this.nestedBlocks.has(loadedEnd)) loadedEnd += this.cacheSize;

    if (startIndex + 2 * listView.pageSize() - 1 >= loadedEnd
      && loadedEnd < this.nestedServerTotal) {
      void this.fetchNestedBlock(listView, loadedEnd);
    }
  }

  // Interroge la base du client et charge le bloc [blockStart, blockStart + cacheSize)
  // du resultat : la fenetre est remplacee, le serveur fait autorite sur son contenu.
  protected async fetchNestedBlock(listView: ListViewComponent<T>, blockStart: number): Promise<void> {
    const clientId = this.varsService.clientId;
    if (!clientId) return;

    // Empreintes prises au moment de la requete : si le contexte ou le tri change pendant
    // le vol, la reponse est abandonnee — la requete suivante refletera le nouvel etat.
    const fingerprint = this.currentNestedQueryFingerprint();
    const sortKey = this.currentNestedSortKey(listView);
    // Le meme changement arrive parfois par deux chemins (refresh du mediateur, fin
    // du debounce de date) : la seconde copie est ignoree tant que la premiere requete
    // n'a pas repondu.
    const requestKey = `${blockStart}|${fingerprint}|${sortKey}`;
    if (this.nestedInFlightKey === requestKey) return;
    this.nestedInFlightKey = requestKey;

    try {
      const response = await this.dataService.queryNested({
        clientId,
        entityName: this.entityMetaData.EntityName,
        pointOfServiceId: this.fkPointOfServiceId,
        pageNumber: blockStart / this.cacheSize + 1,
        pageSize: this.cacheSize,
        search: listView.searchTerm()?.toLowerCase() ?? '',
        searchColumns: listView.getPagination().searchColumns?.join(',') ?? null,
        sortBy: listView.sortBy(),
        sortOrder: listView.sortOrder(),
        filters: this.buildServerFilters()
      });

      if (fingerprint !== this.currentNestedQueryFingerprint()
        || sortKey !== this.currentNestedSortKey(listView)) {
        return;
      }

      // Un changement de tri ou de contexte rend les positions des blocs precedents
      // invalides : on repart du seul bloc qui reflete le nouvel etat.
      if (fingerprint !== this.nestedVerifyFingerprint || sortKey !== this.nestedSortKey) {
        this.nestedBlocks.clear();
      }

      // Les identites connues sont conservees (etat local, modifications) ; le bloc
      // s'ajoute a ceux deja charges au lieu de remplacer le cache.
      const blockItems: T[] = [];
      for (const item of response.items) {
        const existing = this.cache.find(e => this.idsEqual(e.id, item.id));
        if (existing) {
          Object.assign(existing, item);
          existing._entityState = EntityState.Unchanged;
          existing._modifiedProperties.clear();
          blockItems.push(existing);
          continue;
        }

        const hydrated = this.hydrateEntity(item);
        hydrated._entityState = EntityState.Unchanged;
        hydrated._modifiedProperties.clear();
        blockItems.push(hydrated);
      }

      this.nestedBlocks.set(blockStart, blockItems);

      // Le cache est l'union des blocs charges, plus les ajouts WEB-Socket qui n'ont
      // pas encore ete confirmes par un fetch.
      const union = new Map<string, T>();
      for (const items of this.nestedBlocks.values()) {
        for (const e of items) union.set(`${e.id}`, e);
      }
      const pendingAdded = this.cache.filter(e => !union.has(`${e.id}`) && e._entityState === EntityState.Added);
      this.cache = [...union.values(), ...pendingAdded];

      this.nestedServerTotal = response.total;
      // Tronque tant que la base annonce plus de lignes que le cache n'en detient.
      this.nestedTruncated = response.total > union.size;
      this.nestedVerifyFingerprint = fingerprint;
      this.nestedSortKey = sortKey;
      this.updateLastDataTimestampFromCache();
      this.applyPendingChanges();
      this.applyLocalFilterSortPaginate();
    } catch (error) {
      console.error(`Verification du filtrage impossible pour ${this.entityMetaData.EntityDisplayPlural}:`, error);
    } finally {
      // Une requete plus recente a pu prendre le relais pendant le vol : ne pas
      // lui retirer sa cle.
      if (this.nestedInFlightKey === requestKey) this.nestedInFlightKey = null;
    }
  }

  protected applyLocalFilterSortPaginate(keepCurrentIfEmpty = false): void {
    const listView = this.listView();
    if (!listView || !this.isNested) return;

    const offset = (listView.page() - 1) * listView.pageSize();
    const { items: processedData, baseOffset } = this.nestedDisplaySource(listView);
    const relativeStart = Math.max(0, offset - baseOffset);

    const total = this.nestedServerTotal ?? processedData.length;
    const paginatedItems = processedData.slice(relativeStart, relativeStart + listView.pageSize());
    if (keepCurrentIfEmpty && paginatedItems.length === 0) return;
    listView.entities.set({
      items: paginatedItems,
      total,
      truncated: this.nestedTruncated,
      loadedCount: this.cache.length
    });
  }

  // Lignes de la fenetre de la page courante, filtrees et triees comme a l'affichage,
  // avec le decalage absolu de la premiere d'entre elles dans le resultat.
  private nestedDisplaySource(listView: ListViewComponent<T>): { items: T[], baseOffset: number } {
    const offset = (listView.page() - 1) * listView.pageSize();
    let sourceData: T[];
    // Decalage de la source : premier bloc de la plage de la page pour les listes
    // interrogees par fenetres, zero pour un prechargement complet.
    let baseOffset: number;
    if (this.hasActiveQuery()) {
      // La page vit dans les blocs charges qui recouvrent sa plage ; les lignes hors
      // blocs (ajouts WEB-Socket en attente) participent au tri/filtre local.
      const lastIndex = offset + listView.pageSize() - 1;
      const startBlock = Math.floor(offset / this.cacheSize) * this.cacheSize;
      const inBlocks = new Set<T>();
      const regionItems: T[] = [];
      for (let b = startBlock; b <= lastIndex; b += this.cacheSize) {
        const items = this.nestedBlocks.get(b);
        if (items) {
          for (const e of items) {
            regionItems.push(e);
            inBlocks.add(e);
          }
        }
      }
      const pending = this.cache.filter(e => !inBlocks.has(e));
      sourceData = this.filterItems([...regionItems, ...pending]);
      baseOffset = startBlock;
    } else {
      // filteredCache applique les criteres de categorie : sans lui, cocher une puce
      // n'avait aucun effet sur une liste imbriquee.
      sourceData = this.filteredCache;
      baseOffset = 0;
    }
    let processedData = [...sourceData];

    // getPagination() restitue les colonnes resolues ; l'input brut vaut null pour toute
    // liste qui ne declare pas ses colonnes, et la recherche ne filtrait alors rien.
    const pagination = listView.getPagination();

    // Meme normalisation que cote serveur : sans elle, "Elene" ne trouve pas "Elene" accentue.
    const searchTerm = StringHelpers.NormalizeForSearch(listView.searchTerm()).trim();
    const searchColumns = pagination.searchColumns || [];
    if (searchTerm && searchColumns.length > 0) {
      processedData = processedData.filter(item => {
        return searchColumns.some(column => {
          const value = this.getNestedPropertyValue(item, column);
          return value != null && StringHelpers.NormalizeForSearch(value.toString()).includes(searchTerm);
        });
      });
    }

    const sortBy = listView.sortBy();
    const sortOrder = listView.sortOrder();
    const sortableColumns = listView.getPagination().sortableColumns || [];
    if (sortBy && sortableColumns.includes(sortBy)) {
      processedData.sort((a, b) => {
        const valA = this.getNestedPropertyValue(a, sortBy);
        const valB = this.getNestedPropertyValue(b, sortBy);
        if (valA == null && valB == null) return 0;
        if (valA == null) return sortOrder === 'asc' ? -1 : 1;
        if (valB == null) return sortOrder === 'asc' ? 1 : -1;
        if (typeof valA === 'number' && typeof valB === 'number') {
          return sortOrder === 'asc' ? valA - valB : valB - valA;
        }
        if (typeof valA === 'string' && typeof valB === 'string') {
          return sortOrder === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
        }
        return sortOrder === 'asc'
          ? String(valA).localeCompare(String(valB))
          : String(valB).localeCompare(String(valA));
      });
    }

    return { items: processedData, baseOffset };
  }

  // Une creation peut tomber hors de la page affichee : on se place sur la page qui
  // l'accueille et on la selectionne, sinon l'utilisateur ne voit pas ce qu'il a cree.
  private goToItemPage(listView: ListViewComponent<T>, item: T): void {
    let position: number;
    if (this.isNested) {
      const { items, baseOffset } = this.nestedDisplaySource(listView);
      const index = items.indexOf(item);
      if (index === -1) return;
      position = baseOffset + index;
    } else {
      const index = this.filteredCache.indexOf(item);
      if (index === -1) return;
      position = this.cacheStart + index;
    }
    listView.page.set(Math.floor(position / listView.pageSize()) + 1);
    listView.selectedId.set(item.id);
  }

  protected getNestedPropertyValue(item: any, path: string): any {
    return path.split('.').reduce((o, k) => {
      if (o == null) return undefined;
      const match = k.match(/^(.+)\[(\d+)\]$/);
      if (match) {
        const arr = o[match[1]];
        const idx = parseInt(match[2], 10);
        return arr && arr[idx] !== undefined ? arr[idx] : undefined;
      }
      return o[k] !== undefined ? o[k] : undefined;
    }, item);
  }

  showDetails(item: T) {
    this.selectedItem.set(item);
  }

  enterEditModeIfPossible(): void {
    const selected = this.selectedItem();
    let detailComponent = this.itemDetail();
    detailComponent = this.getdetailComponent();
    if (selected && detailComponent) {
      detailComponent.editingEntity.set(selected);
      detailComponent.enterEditMode();
    }
  }

  async deleteSelectedItem(item: T) {
    let suppressConfirm = await this.openSuppressionModal();
    if (suppressConfirm == AlertModalResult.Yes) {
      try {
        await this.dataService.delete(item.id);
        if (this.selectedItem()?.id === item.id) this.selectedItem.set(null);
      } catch (error) {
        console.error(`Delete error: ${this.entityMetaData.EntityDisplayName}`, error);
      }
    }
  }

  getdetailComponent(): GenetricDetailComponent<any> | undefined {
    return undefined;
  }

  async openSuppressionModal(): Promise<AlertModalResult | undefined> {
    let translatedDisplayName = GenericComponent.getTranslationService().instant(`entities.${this.entityMetaData.EntityName.toLowerCase()}.name`);
    const result = await this.alertModalService?.showAlert({
      title: GenericComponent.getTranslationService().instant('vues.alert-modals.suppression.title', { EntityDisplayName: translatedDisplayName }),
      message: GenericComponent.getTranslationService().instant('vues.alert-modals.suppression.message', { EntityDisplayName: translatedDisplayName }),
      messageClasses: 'font-bold',
      yesButtonText: GenericComponent.getTranslationService().instant('buttons.yes'),
      noButtonText: GenericComponent.getTranslationService().instant('buttons.no'),
      Skin: this.Skin
    });
    return result;
  }

  canEdit(item: T): boolean {
    return this.Editable;
  }

  updateItemInCache(item: T): void {
    const hydrated = this.hydrateEntity(item);
    const index = this.cache.findIndex(e => e.id === hydrated.id);
    if (index !== -1) {
      this.cache = [
        ...this.cache.slice(0, index),
        hydrated,
        ...this.cache.slice(index + 1),
      ];
      const listView = this.listView();
      if (listView) {
        if (this.isNested) {
          this.applyLocalFilterSortPaginate();
        } else {
          this.applyLocalPagination((listView.page() - 1) * listView.pageSize(), listView.pageSize());
        }
        listView.refresh();
      }
    }
  }

  postAddedItem(updatedItem: T): void { }

  onItemUpdated(updatedItem: T) {
    const hydrated = this.hydrateEntity(updatedItem);
    hydrated._entityState = EntityState.Unchanged;
    hydrated._modifiedProperties.clear();
    hydrated._changeMap?.clear();
    hydrated._undoState?.clear();

    this.selectedItem.set(hydrated);

    const index = this.cache.findIndex(e => e.id === hydrated.id);
    const isNew = index === -1;
    if (!isNew) {
      this.cache[index] = hydrated;
    } else {
      this.cache.push(hydrated);
      // Le total serveur ne connait pas encore l'ajout : sans increment, le paginateur
      // ne cree pas la page qui doit accueillir la nouvelle ligne.
      if (this.nestedServerTotal !== null) this.nestedServerTotal += 1;
      if (this.cacheServerTotal !== null) this.cacheServerTotal += 1;
      this.postAddedItem(updatedItem);
    }
    this.applySortToCache();
    const listView = this.listView();
    if (listView) {
      if (isNew) this.goToItemPage(listView, hydrated);
      if (this.isNested) {
        this.applyLocalFilterSortPaginate();
      } else {
        this.applyLocalPagination((listView.page() - 1) * listView.pageSize(), listView.pageSize());
      }
    }
  }

  selectSingleItem(id: any) {
    const listView = this.listView();
    if (listView) {
      const db = listView.entities().items.find(e => e.id === id);
      this.selectedItem.set(db || null);
      this.itemSelected.emit(db || null);
    }
  }

  editSelectedItem(db: T) {
    this.componentInstancesService.closeAllComponents();
    this.isModalOpen.set(true);
    this.selectedItem.set(db);
    this.enterEditModeIfPossible();
  }

  openCreateModal() {
    this.componentInstancesService.closeAllComponents();
    const emptyEntity = this.getdetailComponent()?.getEmptyEntity();
    this.getdetailComponent()?.editingEntity.set(emptyEntity || null);
    this.selectedItem.set(emptyEntity || null);
    this.isModalOpen.set(true);
  }

  closeModal(saved: boolean) {
    this.isModalOpen.set(false);
    if (saved === true) this.refreshList();
  }

  refreshList(): void {
    this.listView()?.refresh();
  }

  override onDataRefreshNeeded(): void {
    super.onDataRefreshNeeded();
    this.refreshList();
    const listView = this.listView();
    if (listView) {
      if (this.isNested) {
        this.applyLocalFilterSortPaginate();
      } else {
        this.applyLocalPagination((listView.page() - 1) * listView.pageSize(), listView.pageSize());
      }
    }
  }

  get totalSeen(): number {
    const page = this.listView()?.page() ?? 0;
    const size = this.listView()?.pageSize() ?? 0;
    const total = this.listView()?.entities()?.total ?? 0;
    return Math.min(page * size, total);
  }

  async updateEntityProperty(entity: T, property: keyof T, newValue: any): Promise<void> {
    const updatedEntity = Object.assign(new this.ComponentEntityClass(), entity);
    updatedEntity[property] = newValue;
    try {
      await this.dataService.update(entity.id, updatedEntity, this.getComponentId());
      //this.updateItemInCache(updatedEntity);
      this.onItemUpdated(updatedEntity);
    } catch (error) {
      console.error(`Failed to update ${String(property)}:`, error);
    }
  }

  // Traitement complet des WEB-Socket 
  private handleFilteredEntityChanges(
    initiators: Record<string, string>,
    changes: EntityChangePayload[]
  ): void {

    // === FILTRAGE INITIATEUR ===
    if (initiators['componentId'] === this.componentId
      && initiators['browserId'] === this.varsService.browserId) {
      return;
    }

    const tenantId = initiators['clientId'];
    const currentTenantId = this.varsService.clientId;
    if (tenantId && currentTenantId && tenantId !== currentTenantId) {
      return;
    }

    const posId = initiators['posId'];
    if (this.fkPointOfServiceId != null && posId != null) {
      if (this.fkPointOfServiceId.toString() !== posId) {
        return;
      }
    }

    changes.forEach(change => {
      switch (change.state) {
        case EntityState.Added: {
          //console.log('[DEBUG - 11] CASE: ADDED');
          const newEntity = this.hydrateEntity(change.entity) as T;
          //console.log('[DEBUG - 11] Hydrated entity id:', newEntity.id);

          newEntity._entityState = EntityState.Added;
          newEntity._modifiedBy = change.modifiedBy;
          newEntity._modifiedAt = change.modifiedAt ? new Date(change.modifiedAt) : undefined;

          const exists = this.cache.some(e => this.idsEqual(e.id, newEntity.id));
          //console.log('[DEBUG - 11] Already in cache?', exists);

          if (!exists) {
            this.cache.push(newEntity);
            this.listView()?.addedNotifications.update(list => [...list, newEntity]);
            // Le total serveur fera foi au prochain fetch ; en attendant, l'ajout compte.
            if (this.nestedServerTotal !== null) this.nestedServerTotal += 1;
            //console.log('[DEBUG - 11] ADDED to cache. Cache size now:', this.cache.length);
          }
          break;
        }

        case EntityState.Modified: {

          const existing = this.cache.find(e => this.idsEqual(e.id, change.entity?.id));
          if (!existing) {
            return;
          }

          if (!existing._changeMap) existing._changeMap = new Map();
          existing._modifiedProperties.clear();

          Object.keys(change.entity || {}).forEach(key => {
            if (key === 'id') return;
            const camelKey = this.toCamelCase(key);
            const currentVal = (existing as any)[camelKey];
            const newVal = change.entity[key];

            // ne pas écraser un tableau par un objet/null
            if (Array.isArray(currentVal)) {
              if (newVal == null || !Array.isArray(newVal)) {
                return;
              }
              (existing as any)[camelKey] = newVal;
            } else {
              (existing as any)[camelKey] = newVal;
            }
          });

          // Traiter les scalaires (PropertyChanges non-collection) 
          const scalarChanges = change.propertyChanges?.filter(p => !p.isCollection) || [];
          scalarChanges.forEach(pc => {
            const propName = this.toCamelCase(pc.propertyName);
            const currentVal = (existing as any)[propName];

            //  si la propriété est déjà un tableau, ignorer (données parasites)
            if (Array.isArray(currentVal)) {
              return;
            }

            existing._changeMap!.set(propName, { old: pc.oldValue, new: pc.newValue });
            existing._modifiedProperties.add(propName);
            (existing as any)[propName] = pc.newValue;
          });

          // Traiter les collections (PropertyChanges isCollection = true)
          const collectionPropreties = change.propertyChanges?.filter(p => p.isCollection) || [];

          collectionPropreties.forEach((collProprety, pcIdx) => {
            const propName = this.toCamelCase(collProprety.propertyName);
            existing._modifiedProperties.add(propName);

            const currentCollection = (existing as any)[propName];
            let collectionArray: any[] = Array.isArray(currentCollection) ? [...currentCollection] : [];

            // AJOUTS
            if (collProprety.addedItems?.length) {
              collProprety.addedItems.forEach((item, idx) => {
                const hydrated = this.hydrateNestedEntity(item);
                const alreadyThere = collectionArray.some(e => this.idsEqual(e.id, hydrated.id));
                if (!alreadyThere) {
                  collectionArray.push(hydrated);
                }
              });
            }

            // SUPPRESSIONS
            if (collProprety.removedItems?.length) {
              collProprety.removedItems.forEach((item, idx) => {
                const idxFound = collectionArray.findIndex(e => this.idsEqual(e.id, item.id));
                if (idxFound !== -1) {
                  collectionArray.splice(idxFound, 1);
                }
              });
            }

            // MODIFICATIONS d'éléments existants
            if (collProprety.modifiedItems?.length) {
              collProprety.modifiedItems.forEach((mi, miIdx) => {
                //console.log(`[DEBUG - 11]     MODIFY[${miIdx}] target id:`, mi.entity?.id);
                const idx = collectionArray.findIndex(e => this.idsEqual(e.id, mi.entity?.id));
                //console.log(`[DEBUG - 11]       found at index:`, idx);
                if (idx !== -1 && mi.changes) {
                  mi.changes.forEach(subChange => {
                    const subPropName = this.toCamelCase(subChange.propertyName);
                    //console.log(`[DEBUG - 11]         sub-change ${subPropName}:`, JSON.stringify(subChange.newValue));
                    (collectionArray[idx] as any)[subPropName] = subChange.newValue;
                  });
                }
              });
            }

            (existing as any)[propName] = collectionArray;
          });

          // NavigationChanges (détail par entité liée) 
          change.navigationChanges?.forEach((nc, ncIdx) => {
            const propName = this.toCamelCase(nc.navigationPropertyName);

            existing._modifiedProperties.add(propName);

            const currentCollection = (existing as any)[propName];
            let collectionArray: any[] = Array.isArray(currentCollection) ? [...currentCollection] : [];

            switch (nc.changeType) {
              case NavigationChangeType.Added: {
                const hydrated = this.hydrateNestedEntity(nc.relatedEntity);
                const alreadyThere = collectionArray.some(e => this.idsEqual(e.id, hydrated.id));
                if (!alreadyThere) {
                  collectionArray.push(hydrated);
                }
                break;
              }
              case NavigationChangeType.Removed: {
                const idx = collectionArray.findIndex(e => this.idsEqual(e.id, nc.relatedEntity?.id));
                if (idx !== -1) {
                  collectionArray.splice(idx, 1);
                }
                break;
              }
              case NavigationChangeType.Modified: {
                const idx = collectionArray.findIndex(e => this.idsEqual(e.id, nc.relatedEntity?.id));
                if (idx !== -1 && nc.payloadPropertyChanges) {
                  nc.payloadPropertyChanges.forEach(subPc => {
                    const subPropName = this.toCamelCase(subPc.propertyName);
                    (collectionArray[idx] as any)[subPropName] = subPc.newValue;
                  });
                }
                break;
              }
            }

            (existing as any)[propName] = collectionArray;
          });



          // FinalisATION
          existing._entityState = EntityState.Modified;
          existing._modifiedBy = change.modifiedBy;
          existing._modifiedAt = change.modifiedAt ? new Date(change.modifiedAt) : undefined;

          // Notification au détail si c'est l'entité sélectionnée
          if (this.idsEqual(existing.id, this.selectedItem()?.id)) {
            //console.log('[DEBUG - 11] SELECTED item, notifying detail...');
            this.selectedItem.set({ ...existing });
            this.getdetailComponent()?.onEntityChanged();
            //console.log('[DEBUG - 11] Detail notified');
          }
          break;
        }

        case EntityState.Deleted: {
          const deleted = this.cache.find(e => this.idsEqual(e.id, change.entity?.id));
          if (deleted) {
            deleted._entityState = EntityState.Deleted;
            deleted._modifiedBy = change.modifiedBy;
            deleted._modifiedAt = change.modifiedAt ? new Date(change.modifiedAt) : undefined;
          }
          break;
        }
      }
    });

    // Refresh UI 

    // Nouvelle référence du cache
    this.cache = this.cache.map(e => ({ ...e }));

    // La copie cree de nouvelles instances : les blocs imbriques doivent pointer sur
    // elles, sinon l'affichage continuerait a lire les objets d'avant la copie.
    const byId = new Map(this.cache.map(e => [`${e.id}`, e]));
    for (const [blockKey, items] of this.nestedBlocks) {
      this.nestedBlocks.set(blockKey, items.map(it => byId.get(`${it.id}`) ?? it));
    }

    //  Mettre à jour les items affichés dans le listView
    const listView = this.listView();
    if (listView) {
      const currentEntities = listView.entities();
      const updatedItems = currentEntities.items.map(item => {
        const cached = this.cache.find(c => this.idsEqual(c.id, item.id));
        return cached ? { ...cached } : item;
      });

      listView.entities.set({
        ...currentEntities,
        items: updatedItems
      });

      if (this.isNested) {
        // Page hors fenetre : garder les lignes courantes plutot qu'un slice vide.
        this.applyLocalFilterSortPaginate(this.hasActiveQuery() && !this.nestedCacheCoversPage(listView));
      } else {
        this.applyLocalPagination((listView.page() - 1) * listView.pageSize(), listView.pageSize());
      }
    }

    // Notifier le detail-component
    const selected = this.selectedItem();
    if (selected) {
      const updated = this.cache.find(e => this.idsEqual(e.id, selected.id));
      if (updated) {

        this.selectedItem.set({ ...updated }); // Nouvelle référence
      }
    }

  }


  private toCamelCase(str: string): string {
    if (!str) return str;
    return str.charAt(0).toLowerCase() + str.slice(1);
  }

  private hydrateNestedEntity(data: any): any {
    const instance = { ...data };
    instance._entityState = EntityState.Unchanged;
    instance._modifiedProperties = new Set<string>();
    return instance;
  }

  protected hydrateEntity(data: any): T {
    const instance = new this.ComponentEntityClass();
    Object.assign(instance, data);
    instance._entityState = EntityState.Unchanged;
    instance._modifiedProperties = new Set<string>();
    return instance;
  }

  public handleRowDismiss(entity: T): void {
    if (entity._entityState === EntityState.Deleted) {
      this.removeDeletedEntity(entity);
    } else {
      this.dismissEntityState(entity);
    }
  }

  private removeDeletedEntity(entity: T): void {
    this.removeLocalDeletedEntity(entity);
  }

  private removeLocalDeletedEntity(entity: T): void {
    const index = this.cache.findIndex(e => e.id === entity.id);
    if (index !== -1) {
      this.cache.splice(index, 1);
      const listView = this.listView();
      if (listView) {
        if (this.isNested) {
          this.applyLocalFilterSortPaginate();
        } else {
          this.applyLocalPagination((listView.page() - 1) * listView.pageSize(), listView.pageSize());
        }
      }
    }
  }

  public dismissEntityState(entity: T): void {
    this.dismissLocalEntityState(entity);
  }

  private dismissLocalEntityState(entity: T): void {
    //const cached = this.cache.find(e => e.id === entity.id);
    const cached = this.cache.find(e => this.idsEqual(e.id, entity.id));
    if (cached) {
      cached._entityState = EntityState.Unchanged;
      cached._modifiedProperties.clear();
      cached._modifiedBy = undefined;
      cached._modifiedAt = undefined;
      const listView = this.listView();
      if (listView) {
        if (this.isNested) {
          this.applyLocalFilterSortPaginate();
        } else {
          this.applyLocalPagination((listView.page() - 1) * listView.pageSize(), listView.pageSize());
        }
      }
    }
  }

  private applySortToCache(): void {
    const listView = this.listView();
    if (!listView) return;
    const sortBy = listView.sortBy();
    const sortOrder = listView.sortOrder();
    const sortableColumns = listView.getPagination().sortableColumns || [];
    if (sortBy && sortableColumns.includes(sortBy)) {
      this.cache.sort((a, b) => {
        const valA = this.getNestedPropertyValue(a, sortBy);
        const valB = this.getNestedPropertyValue(b, sortBy);
        if (valA == null && valB == null) return 0;
        if (valA == null) return sortOrder === 'asc' ? -1 : 1;
        if (valB == null) return sortOrder === 'asc' ? 1 : -1;
        if (typeof valA === 'number' && typeof valB === 'number') {
          return sortOrder === 'asc' ? valA - valB : valB - valA;
        }
        if (typeof valA === 'string' && typeof valB === 'string') {
          return sortOrder === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
        }
        return sortOrder === 'asc'
          ? String(valA).localeCompare(String(valB))
          : String(valB).localeCompare(String(valA));
      });
    }
  }

  isSameDay(a: Date | string, b: Date | string | null | undefined): boolean {
    if (!b) return false;
    const d1 = new Date(a);
    const d2 = new Date(b);
    return d1.getFullYear() === d2.getFullYear()
      && d1.getMonth() === d2.getMonth()
      && d1.getDate() === d2.getDate();
  }

  onFilterSelectionChange(updatedOptions: FilterOption[]): void {
    const selectedOptions = updatedOptions.filter(opt => opt.isSelected);
    const newCriterias: IFilterCriteria[] = selectedOptions.map(opt => ({
      fieldName: opt.fieldName,
      fieldValue: opt.id.includes('_') ? opt.id.split('_')[1] : opt.id,
      filterOperator: IFilterOperator.equals,
      filterLogic: 'or'
    }));
    this.filterCriterias.set(newCriterias);
    this.selectedFilterOptionIds.set(selectedOptions.map(opt => opt.id));
    this.persistFilterState();

    const listView = this.listView();
    if (listView) {
      listView.page.set(1);
      listView.refreshCounter.update(v => v + 1);
    }
  }

  getFormatedDateValue(value: Date | string | null | undefined) {
    return GenericComponent.GetFormatedDateValue(value, this.translationService.instant('app.short-local-date-time'), this.translationService.instant('app.time-output-format'));
  }

  getFormatedTimeValue(value: Date | string | null | undefined) {
    if (!value) return "";
    const date = new Date(value);
    let format = this.translationService.instant('app.time-output-format');
    if (!format || format === 'app.time-output-format') {
      const shortFormat = this.translationService.instant('app.short-local-date-time').toLowerCase().trim();
      format = shortFormat.endsWith(' tt') ? "12h" : "24h";
    }
    const hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    if (format.trim() == "12h") {
      const h12 = (hours % 12 || 12).toString().padStart(2, '0');
      const suffix = hours >= 12 ? "PM" : "AM";
      return `${h12}:${minutes} ${suffix}`;
    }
    return `${hours.toString().padStart(2, '0')}:${minutes}`;
  }


  // Comparaison d'ids plus robuste pour les WS 
  private idsEqual(id1: any, id2: any): boolean {
    if (id1 === id2) return true;
    if (id1 == null || id2 == null) return false;

    // Les deux sont des tableaux (clés composites)
    if (Array.isArray(id1) && Array.isArray(id2)) {
      if (id1.length !== id2.length) return false;
      return id1.every((val, idx) => val === id2[idx]);
    }

    // L'un est tableau, l'autre est un objet avec id tableau (cas hydrateNestedEntity)
    if (Array.isArray(id1) && typeof id2 === 'object' && Array.isArray(id2.id)) {
      return this.idsEqual(id1, id2.id);
    }
    if (Array.isArray(id2) && typeof id1 === 'object' && Array.isArray(id1.id)) {
      return this.idsEqual(id1.id, id2);
    }

    // L'un est tableau, l'autre scalaire
    if (Array.isArray(id1) && !Array.isArray(id2)) {
      return id1[0] === id2 || id1.includes(id2);
    }
    if (!Array.isArray(id1) && Array.isArray(id2)) {
      return id2[0] === id1 || id2.includes(id1);
    }

    return false;
  }




}
